import React, { useEffect, useState } from "react";
import {
  AppTopNaviDivider,
  AppTopNavi,
  AppTopNaviItem,
  Button,
} from "@abb/abb-common-ux-react";
import avtar from "../../assets/Avatar.png";
import logo from "../../assets/Logo.png";
import { useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import { getConfig } from "../../utils/configUtils";
import { 
  isNetZeroVisible, 
  isEAVisible, 
  isEMVisible, 
  isDRDashboardVisible, 
  isSolarEnergyVisible, 
  isProoferToolVisible,
  isCybershieldModuleVisible,
  isWNTModuleVisible,
  getUIConfig 
} from "../../utils/uiConfigUtils";
import { isSolutionVisible, getSolutionVisibility } from "../../utils/solutionVisibilityUtils";
import { isSandboxEnvironment } from "../../utils/environmentConfig";
import "./navbar.css";

// Conditionally import customSolutionUtils only in sandbox
let getCustomSolutions = () => [];
if (isSandboxEnvironment()) {
  try {
    getCustomSolutions = require("../../utils/customSolutionUtils").getCustomSolutions;
  } catch (e) {
    console.warn("Custom solutions not available");
  }
}

function Navbar() {
  const getCurrentMenu = localStorage.getItem("current-page");
  const [monochrome, setMonochrome] = React.useState(false);
  const [activeItem, setActiveItem] = React.useState(
    getCurrentMenu ? getCurrentMenu : "Dashboard"
  );
  const [solutionsConfig, setSolutionsConfig] = useState(getConfig());
  const [configLoaded, setConfigLoaded] = useState(false);
  const [visibilityRefresh, setVisibilityRefresh] = useState(0); // Force re-render trigger
  const [customSolutions, setCustomSolutions] = useState([]);
  const navigate = useNavigate();

  // Load UI configuration and solution visibility from DynamoDB on mount
  useEffect(() => {
    const loadConfigurations = async () => {
      try {
        console.log('🔄 Navbar: Loading configurations from DynamoDB...');
        await Promise.all([
          getUIConfig(), // Load UI module visibility
          getSolutionVisibility() // Load solution visibility
        ]);
        setConfigLoaded(true);
        console.log('✅ Navbar: Configurations loaded from DynamoDB');
        
        // Force re-render to apply visibility changes
        setSolutionsConfig(getConfig());
        
        // Load custom solutions
        setCustomSolutions(getCustomSolutions());
      } catch (error) {
        console.error('❌ Navbar: Error loading configurations:', error);
        setConfigLoaded(true); // Continue with cached config
        setCustomSolutions(getCustomSolutions()); // Load custom solutions anyway
      }
    };
    loadConfigurations();
  }, []);

  // Listen for configuration changes
  useEffect(() => {
    const handleStorageChange = async () => {
      try {
        console.log('🔄 Navbar: Reloading configurations...');
        await Promise.all([
          getUIConfig(),
          getSolutionVisibility()
        ]);
        setSolutionsConfig(getConfig());
        setVisibilityRefresh(prev => prev + 1); // Force re-render
        setCustomSolutions(getCustomSolutions()); // Reload custom solutions
        console.log('✅ Navbar: Configurations reloaded');
      } catch (error) {
        console.error('❌ Navbar: Error reloading configurations:', error);
        setSolutionsConfig(getConfig());
        setVisibilityRefresh(prev => prev + 1); // Force re-render anyway
        setCustomSolutions(getCustomSolutions()); // Reload custom solutions anyway
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom config update event
    window.addEventListener('configUpdated', handleStorageChange);
    
    // Listen for solution visibility updates
    window.addEventListener('solutionVisibilityUpdated', handleStorageChange);
    
    // Listen for custom solution updates
    window.addEventListener('customSolutionUpdated', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('configUpdated', handleStorageChange);
      window.removeEventListener('solutionVisibilityUpdated', handleStorageChange);
      window.removeEventListener('customSolutionUpdated', handleStorageChange);
    };
  }, []);

  const logout = async () => {
    localStorage.clear();
    localStorage.setItem("isLogin", "false");
    try {
      await Auth.signOut();
      console.log("User logged out");
    } catch (error) {
      console.log("Error signing out:", error);
    }
    navigate("../");
  };
  return (
    <div className="header-container">
      <div className="brand">
        <div>
          <img className="avtar-icon" src={logo} alt="" />
        </div>

        <div className="brand-name">
          {/* <AppTopNaviDivider  /> */}
          <div className="project-name">
            <AppTopNaviDivider />
            <div>
              <div className="title-name">
                CogniEN<sup id="super">TM</sup>
              </div>
              {/* <div>DR Monitoring System</div> */}
            </div>
          </div>
        </div>
      </div>

      <AppTopNavi monochrome={monochrome} allowScroll={true}>
        {solutionsConfig.solutions.drDashboard.enabled && isDRDashboardVisible() && isSolutionVisible('drDashboard') && (
          <AppTopNaviItem
            text={solutionsConfig.solutions.drDashboard.name}
            active={activeItem === "Dashboard"}
            onClick={() => {
              setActiveItem("Dashboard");
              navigate(solutionsConfig.solutions.drDashboard.route);
              localStorage.setItem("current-page", "dashboard");
            }}
          />
        )}

        {solutionsConfig.solutions.emWidgets.enabled && isEMVisible() && isSolutionVisible('em') && (
          <AppTopNaviItem
            text={solutionsConfig.solutions.emWidgets.name}
            active={activeItem === "em"}
            onClick={() => {
              setActiveItem("em");
              navigate(solutionsConfig.solutions.emWidgets.route);
              localStorage.setItem("current-page", "em");
            }}
          />
        )}

        {solutionsConfig.solutions.netZero.enabled && isNetZeroVisible() && isSolutionVisible('netZero') && (
          <AppTopNaviItem
            text={solutionsConfig.solutions.netZero.name}
            active={activeItem === "nz"}
            onClick={() => {
              setActiveItem("nz");
              navigate(solutionsConfig.solutions.netZero.route);
              localStorage.setItem("current-page", "nz");
            }}
          />
        )}

        {solutionsConfig.solutions.microgridEnergyOptimization.enabled && (
          <AppTopNaviItem
            text={solutionsConfig.solutions.microgridEnergyOptimization.name}
            active={activeItem === "microgrid-opt"}
            onClick={() => {
              setActiveItem("microgrid-opt");
              navigate(solutionsConfig.solutions.microgridEnergyOptimization.route);
              localStorage.setItem("current-page", "microgrid-opt");
            }}
          />
        )}

        {solutionsConfig.solutions.wideAreaNetworkTwin.enabled && isSolutionVisible('wnt') &&
         (isWNTModuleVisible('singleLineDiagram') || isWNTModuleVisible('geographicalMap') || 
          isWNTModuleVisible('digitalTwin') || isWNTModuleVisible('solarOutputPrediction')) && (
          <AppTopNaviItem
            text={solutionsConfig.solutions.wideAreaNetworkTwin.name}
            active={activeItem === "wnt"}
            onClick={() => {
              setActiveItem("wnt");
              navigate(solutionsConfig.solutions.wideAreaNetworkTwin.route);
              localStorage.setItem("current-page", "wnt");
            }}
          />
        )}

        {solutionsConfig.solutions.cybershield.enabled && isSolutionVisible('cybershield') && 
         (isCybershieldModuleVisible('posture') || isCybershieldModuleVisible('rim')) && (
          <AppTopNaviItem
            text={solutionsConfig.solutions.cybershield.name}
            active={activeItem === "cyber-shield"}
            onClick={() => {
              setActiveItem("cyber-shield");
              navigate(solutionsConfig.solutions.cybershield.route);
              localStorage.setItem("current-page", "cyber-shield");
            }}
          />
        )}

        {solutionsConfig.solutions.prooferTool.enabled && isProoferToolVisible() && isSolutionVisible('prooferTool') && (
          <AppTopNaviItem
            text={solutionsConfig.solutions.prooferTool.name}
            active={activeItem === "proofer-tool"}
            onClick={() => {
              setActiveItem("proofer-tool");
              navigate(solutionsConfig.solutions.prooferTool.route);
              localStorage.setItem("current-page", "proofer-tool");
            }}
          />
        )}

        {solutionsConfig.solutions.cognien.enabled && isSolutionVisible('cognien') && (
          <AppTopNaviItem
            text={solutionsConfig.solutions.cognien.name}
            active={activeItem === "cognien"}
            onClick={() => {
              setActiveItem("cognien");
              navigate(solutionsConfig.solutions.cognien.route);
              localStorage.setItem("current-page", "cognien");
            }}
          />
        )}

        {solutionsConfig.solutions.evciEds.enabled && isEAVisible() && isSolutionVisible('evciEds') && (
          <AppTopNaviItem
            text={solutionsConfig.solutions.evciEds.name}
            active={activeItem === "evci-eds"}
            onClick={() => {
              setActiveItem("evci-eds");
              navigate(solutionsConfig.solutions.evciEds.route);
              localStorage.setItem("current-page", "evci-eds");
            }}
          />
        )}

        {solutionsConfig.solutions.solarEnergy.enabled && isSolarEnergyVisible() && isSolutionVisible('solarEnergy') && (
          <AppTopNaviItem
            text={solutionsConfig.solutions.solarEnergy.name}
            active={activeItem === "solar-energy"}
            onClick={() => {
              setActiveItem("solar-energy");
              navigate(solutionsConfig.solutions.solarEnergy.route);
              localStorage.setItem("current-page", "solar-energy");
            }}
          />
        )}
        
        {/* Custom Solution Tabs */}
        {customSolutions.map((solution) => (
          <AppTopNaviItem
            key={solution.id}
            text={solution.name}
            active={activeItem === solution.id}
            onClick={() => {
              setActiveItem(solution.id);
              navigate(`/custom-solution/${solution.id}`);
              localStorage.setItem("current-page", solution.id);
            }}
          />
        ))}
     
     

      </AppTopNavi>




      {isSandboxEnvironment() && (
        <Button
          text="Edit Configuration"
          onClick={() => navigate("../admin/edit-modules")}
          shape="pill"
          type="primary"
        />
      )}
      <Button
        text="Logout"
        onClick={logout}
        shape="pill"
        type="primary-black"
      />
      <div className="profile">
        <img className="avtar-icon" src={avtar} alt="" />
      </div>
    </div>
  );
}

export default Navbar;
